/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useMemo, useState, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Typography, Pagination, Empty, Spin } from 'antd';
import { useIntl } from 'react-intl';
import { useStyles, ThemedStyleFn } from '~src/hooks/useStyles';
import { useSharedTenderQuestions } from '~src/hooks/api-hooks/useSharedTenderQuestions';
import { useSharedTenderMyQuestions } from '~src/hooks/api-hooks/useSharedTendersMyQuestions';
import IconButton from '~components/_common/IconButton/IconButton';
import QuestionsList from '~components/atoms/Questions/QuestionsList';
import QuestionsFilter, {
  QuestionsFilterOptions,
} from '~components/atoms/Questions/QuestionsFilter/QuestionsFilter';
import { QUESTIONS_DEFAULT_PAGE_SIZE } from '~src/config';
import { SearchContext } from '~src/contexts/SearchContext';
import { filterByQuery } from '~src/utils/searchFilters';
import moment from 'moment';

type QuestionsBlockProps = {
  shareKey?: string;
  shareId: string;
  onClose?: () => void;
  onAddQuestion?: () => void;
  tenderDetails?: any;
};

const QuestionsBlock: React.FC<QuestionsBlockProps> = ({
  shareId,
  shareKey,
  onClose,
  onAddQuestion,
  tenderDetails,
}) => {
  const styles = useStyles(styleFn);
  const intl = useIntl();
  const history = useHistory();
  const { query } = useContext(SearchContext);

  const [currentFilterOption, setCurrentFilterOption] = useState(
    QuestionsFilterOptions.ALL
  );

  const shareIdKey = useMemo(() => `${shareId}-${shareKey}`, [
    shareId,
    shareKey,
  ]);

  const isAddDisabled = useMemo(() => {
    const d = tenderDetails;
    if (!d || !d.qa_on || d.state >= 6) return true;
    const now = moment();
    const isEnded =
      d.qa_stop || d.deadline_at
        ? moment.unix(d.qa_stop || d.deadline_at).isBefore(now)
        : false;
    return isEnded;
  }, [tenderDetails]);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 5,
    total: 0,
  });

  const {
    data: allData,
    isFetching: isAllFetching,
    refetch: refetchAll,
  } = useSharedTenderQuestions(
    {
      share_id: shareId,
      share_key: shareKey,
      skip: (pagination.current - 1) * pagination.pageSize,
      take: pagination.pageSize,
    },
    {
      onSuccess: (d) => setPagination((p) => ({ ...p, total: d.totalCount })),
      enabled: false,
    }
  );

  const {
    data: myData,
    isFetching: isMyFetching,
    refetch: refetchMy,
  } = useSharedTenderMyQuestions(
    {
      share_id: shareId,
      share_key: shareKey,
      skip: (pagination.current - 1) * pagination.pageSize,
      take: pagination.pageSize,
    },
    {
      onSuccess: (d) => setPagination((p) => ({ ...p, total: d.totalCount })),
      enabled: false,
    }
  );

  useEffect(() => {
    setPagination((p) => ({ ...p, current: 1 }));
  }, [currentFilterOption, query]);

  useEffect(() => {
    currentFilterOption === QuestionsFilterOptions.MY
      ? refetchMy()
      : refetchAll();
  }, [currentFilterOption, pagination.current, query]);

  const items = useMemo(() => {
    const raw =
      (currentFilterOption === QuestionsFilterOptions.MY
        ? myData?.items
        : allData?.items) || [];
    return query
      ? raw.filter(
          (q) =>
            filterByQuery(q.question, query) ||
            (q.answers || []).some((a) => filterByQuery(a.answer, query))
        )
      : raw;
  }, [myData, allData, currentFilterOption, query]);

  const isLoading = isAllFetching || isMyFetching;

  return (
    <div css={styles.miniBlockWrapper}>
      <div css={styles.header}>
        <div css={styles.topLine}>
          <Typography.Text strong css={styles.title}>
            {intl.formatMessage({ id: 'questions-block-title' })}
          </Typography.Text>
          <div css={styles.actions}>
            <IconButton
              click={() => history.push(`/my-tenders/${shareIdKey}/questions`)}
              icon={['far', 'expand-alt']}
              customStyle={styles.actionIcon}
            />
            <IconButton
              click={isAddDisabled ? undefined : onAddQuestion}
              icon={['far', 'plus-circle']}
              disabled={isAddDisabled}
              customStyle={
                isAddDisabled ? { opacity: 0.3 } : { color: '#1890ff' }
              }
            />
            <IconButton
              click={onClose}
              icon={['far', 'times']}
              customStyle={styles.actionIcon}
            />
          </div>
        </div>

        <div css={styles.filterContainer}>
          <QuestionsFilter
            currentFilterOption={currentFilterOption}
            onCurrentFilterOptionChange={setCurrentFilterOption}
          />
        </div>
      </div>

      <div css={styles.scrollContent}>
        {isLoading ? (
          <div css={styles.center}>
            <Spin />
          </div>
        ) : items.length > 0 ? (
          <QuestionsList
            questions={items}
            isLoading={false}
            split={false}
            hideInternalPagination={true}
            insideOfBtn
            isOnTender={true}
            questionItemConfig={{
              withStatus: currentFilterOption === QuestionsFilterOptions.MY,
            }}
          />
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </div>

      <div css={styles.footer}>
        <Pagination
          size="small"
          current={pagination.current}
          total={pagination.total}
          pageSize={pagination.pageSize}
          onChange={(page) => setPagination((p) => ({ ...p, current: page }))}
          showSizeChanger={false}
          hideOnSinglePage
        />
      </div>
    </div>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  miniBlockWrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '380px',
    height: '550px',
    backgroundColor: '#fff',
  },
  header: {
    padding: '16px',
    borderBottom: '1px solid #f0f0f0',
    background: '#fff',
  },
  topLine: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  title: { fontSize: '15px', color: '#1a1a1a' },
  actions: { display: 'flex', gap: '8px' },
  actionIcon: { color: '#8c8c8c', fontSize: '14px' },
  filterContainer: {
    '.ant-radio-group': { width: '100%', display: 'flex' },
    '.ant-radio-button-wrapper': { flex: 1, textAlign: 'center' },
  },
  scrollContent: {
    flex: 1,
    overflowY: 'auto',
    padding: '12px',
    '&::-webkit-scrollbar': { width: '4px' },
    '&::-webkit-scrollbar-thumb': {
      background: '#d9d9d9',
      borderRadius: '4px',
    },
  },
  footer: {
    padding: '12px',
    borderTop: '1px solid #f0f0f0',
    display: 'flex',
    justifyContent: 'center',
  },
  center: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
  },
});

export default QuestionsBlock;
